/**
 * Roadmap — feedback that survived triage, ranked by the people who want it.
 *
 * The vote is optimistic and one-way: there is no un-vote endpoint, so a failed POST
 * rolls the row back rather than leaving a button the player cannot press again.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import { api, type RoadmapEntry } from '../../net/api.js';
import { ListState, Section, errText, loadList, useLoader } from './common.js';

export interface RoadmapProps {
  nonce: number;
  token: string;
}

export function Roadmap(props: RoadmapProps): VNode {
  const { token } = props;
  const loaded = useLoader<RoadmapEntry[]>(
    () => loadList<RoadmapEntry>('/api/roadmap', token, 'roadmap'),
    `roadmap:${String(props.nonce)}:${token}`,
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const list = loaded.data ?? [];

  const vote = (entry: RoadmapEntry): void => {
    const current = loaded.data;
    if (!current || busy || entry.voted === true) return;
    setBusy(true);
    setNote('');
    loaded.set(
      current.map((e) =>
        e.id === entry.id ? { ...e, voted: true, votes: (e.votes ?? 0) + 1 } : e,
      ),
    );
    void (async () => {
      try {
        await api(`/api/roadmap/${encodeURIComponent(String(entry.id))}/vote`, {
          method: 'POST',
          token,
        });
      } catch (err) {
        loaded.set(current);
        setNote(errText(err, 'Your vote did not reach the board.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div class="hub-pane">
      <Section title="Roadmap" hint="What we heard, and what we are weighing">
        {note.length > 0 ? <p class="hub-error">{note}</p> : null}
        <ListState
          loading={loaded.loading}
          offline={!loaded.loading && loaded.data === null}
          empty={list.length === 0}
          emptyText="Nothing on the board yet. Send something through the Terminal and it may end up here."
        />
        <div class="hub-list">
          {list.map((e) => {
            const body = e.text ?? e.body ?? '';
            const voted = e.voted === true;
            return (
              <article key={String(e.id)} class="hub-card hub-roadmap">
                <button
                  type="button"
                  class={`hub-vote${voted ? ' on' : ''}`}
                  disabled={voted || busy}
                  aria-label={voted ? 'Already upvoted' : 'Upvote'}
                  onClick={() => {
                    vote(e);
                  }}
                >
                  <span class="hub-vote-chev">^</span>
                  <span class="hub-vote-num">{String(e.votes ?? 0)}</span>
                </button>
                <div class="hub-roadmap-text">
                  <h3 class="hub-card-title">{e.title ?? String(e.id)}</h3>
                  {body.length > 0 ? <p class="hub-card-blurb">{body}</p> : null}
                  <div class="hub-card-foot">
                    {typeof e.status === 'string' && e.status.length > 0 ? (
                      <span class="hub-tag">{e.status}</span>
                    ) : null}
                    {typeof e.category === 'string' && e.category.length > 0 ? (
                      <span class="hub-tag">{e.category}</span>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
